/**
 * @file toolbar-icon-button.tsx
 *
 * A thin wrapper around the MD3 `IconButton` component, pre-configured
 * for use inside Toolbar slots.
 *
 * Supports the three emphasis variants described in the MD3 Flexibility & slots
 * spec, plus narrow/wide sizing for asymmetric visual hierarchy.
 *
 * @see https://m3.material.io/components/toolbars/guidelines (Anatomy → Flexibility & slots)
 * @see https://m3.material.io/components/icon-buttons/overview
 */

import * as React from "react";

import { cn } from "../../lib/utils";
import type { MD3SlotWidth } from "../../types/md3";
import { type BaseIconButtonProps, IconButton } from "../buttons/icon-button";
import { useToolbarContext } from "./toolbar-context";
import {
	TOOLBAR_SPRING_TRANSITION,
	ToolbarIconButtonTokens,
} from "./toolbar-tokens";

export interface ToolbarIconButtonProps
	extends Omit<BaseIconButtonProps, "colorStyle" | "size" | "shape"> {
	/**
	 * Visual emphasis variant.
	 *
	 * ⚠️ Avoid emphasising more than one action at a time.
	 * @default "standard"
	 */
	emphasis?: ToolbarIconButtonVariant;
	/**
	 * Slot width of the toolbar icon button per MD3 Expressive specification.
	 * Controls how much horizontal space the button occupies in the toolbar.
	 * - `"narrow"` – minimum width slot (40px), for compact actions.
	 * - `"default"` – standard square slot (48px).
	 * - `"wide"` – extended slot (64px), for primary emphasis actions.
	 * @default "default"
	 */
	width?: MD3SlotWidth;
	/**
	 * Whether the icon button is in a selected/active toggle state.
	 */
	selected?: boolean;
	/**
	 * Change the default rendered element to the one passed as a child, merging their props and behavior.
	 */
	asChild?: boolean;
	/** Icon content — typically a single SVG icon component. */
	children: React.ReactNode;
	/**
	 * Accessible label — **REQUIRED** because icon buttons have no visible text.
	 */
	"aria-label": string;
}

/**
 * Visual emphasis variant for a toolbar icon button.
 *
 * MD3 guideline: *"Use different icon button color styles, such as filled,
 * tonal, and standard."*
 *
 * ⚠️ **Avoid emphasising more than one action at a time.**
 * Use `"filled"` or `"tonal"` for the single highest-priority action and
 * keep all other buttons as `"standard"`.
 */
export type ToolbarIconButtonVariant = "standard" | "tonal" | "filled";

const TOOLBAR_ICON_BUTTON_WIDTH: Record<MD3SlotWidth, string> = {
	narrow: "w-[40px]",
	default: "w-[48px]",
	wide: "w-[64px]",
};

const VARIANT_TO_COLOR_STYLE: Record<
	ToolbarIconButtonVariant,
	"standard" | "filled" | "tonal"
> = {
	standard: "standard",
	tonal: "tonal",
	filled: "filled",
};

/**
 * An MD3 icon button optimised for use inside Toolbar slots.
 *
 * Wraps the project's `IconButton` and adds:
 * - Three emphasis styles (`standard`, `tonal`, `filled`) via `emphasis` prop.
 * - Three width sizes (`narrow`, `default`, `wide`) for visual hierarchy.
 * - Support for `selected` state (Toggleable Icon Button per MD3 spec).
 * - Support for `asChild` slot delegation.
 * - Always `rounded-full` shape (required for floating toolbars per MD3 spec).
 * - Always 48dp height to meet MD3 accessibility touch-target requirements.
 * - Material Design 3 Expressive spring motion physics (`TOOLBAR_SPRING_TRANSITION`)
 *   synchronized with `ButtonDistribute` for responsive press scale and width expansion.
 *
 * @example
 * ```tsx
 * // Standard (default) — use for most actions
 * <ToolbarIconButton aria-label="Share">
 *   <ShareIcon />
 * </ToolbarIconButton>
 *
 * // Toggleable state
 * <ToolbarIconButton selected={isSelected} aria-label="Bookmark" onClick={() => setIsSelected(!isSelected)}>
 *   <BookmarkIcon />
 * </ToolbarIconButton>
 * ```
 *
 * @see https://m3.material.io/components/toolbars/guidelines
 */
export const ToolbarIconButton = React.forwardRef<
	HTMLButtonElement,
	ToolbarIconButtonProps
>(
	(
		{
			emphasis = "standard",
			width = "default",
			selected,
			asChild,
			className,
			transition,
			whileTap,
			...props
		},
		ref,
	) => {
		const context = useToolbarContext();
		const widthClass = TOOLBAR_ICON_BUTTON_WIDTH[width];
		const colorStyle = VARIANT_TO_COLOR_STYLE[emphasis];

		const iconButtonProps =
			selected !== undefined
				? ({
						variant: "toggle" as const,
						selected: Boolean(selected),
					} as const)
				: ({
						variant: "default" as const,
					} as const);

		const motionProps = asChild
			? {
					...(transition !== undefined && { transition }),
					...(whileTap !== undefined && { whileTap }),
				}
			: {
					transition: transition ?? TOOLBAR_SPRING_TRANSITION,
					whileTap: whileTap ?? { scale: 0.95 },
				};

		return (
			<IconButton
				ref={ref}
				asChild={asChild}
				colorStyle={colorStyle}
				size="md"
				shape="round"
				iconSize={ToolbarIconButtonTokens.IconSize}
				{...motionProps}
				{...iconButtonProps}
				className={cn(
					"h-[48px]",
					widthClass,
					emphasis === "standard" &&
						!selected &&
						"text-current hover:bg-current/8 active:bg-current/12",
					emphasis === "standard" &&
						selected &&
						"bg-current/16 text-current hover:bg-current/20 active:bg-current/24",
					context?.itemClassName,
					className,
				)}
				{...props}
			/>
		);
	},
);

ToolbarIconButton.displayName = "ToolbarIconButton";
